import ModalSurface from '@/components/ModalSurface';
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, UserPlus, Trash2, Save, ShieldCheck, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HubHeader from "@/components/HubHeader";
import { Toaster } from "@/components/ui/sonner";
import TablePager from "@/components/TablePager";

const ROLES = ["admin", "manager", "scientist", "viewer", "user"];
const MODULES = [
  { key: "bd", label: "International BD" },
  { key: "hrms", label: "Human Resources" },
  { key: "crm", label: "Customer CRM" },
  { key: "portfolio", label: "Product & Service Portfolio" },
  { key: "partners", label: "Partners" },
  { key: "formulations", label: "Formulations" },
  { key: "dossiers", label: "Dossiers" },
  { key: "clinical-intelligence", label: "Clinical Intelligence" },
];

/**
 * Global admin panel — /admin
 * Manage user roles + module access; invite new users.
 * Admin-only (403 redirects back to the Hub).
 */
export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // { [id]: bool }
  const [showInvite, setShowInvite] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());   // ids ticked for bulk delete
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: { page, page_size: pageSize, search: search || undefined },
      });
      setUsers(data.items || data.users || []);
      setTotalUsers(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistic in-place row edit
  const updateLocal = (id, patch) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const persistUser = async (u) => {
    setSaving((s) => ({ ...s, [u.id]: true }));
    try {
      await api.patch(`/admin/users/${u.id}`, {
        role: u.role,
        modules: u.modules,
        company_id: u.company_id || undefined,
        module_roles: u.module_roles || {},
        disabled: Boolean(u.disabled),
      });
      toast.success(`Saved ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      // Rollback: reload authoritative state
      load();
    } finally {
      setSaving((s) => ({ ...s, [u.id]: false }));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      // If we just emptied the current page, step back one so we don't
      // land on a phantom "0 of 0" screen.
      if (users.length === 1 && page > 1) setPage(page - 1);
      else load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllVisible = () => setSelected((s) => {
    const next = new Set(s);
    const visibleIds = users.map((u) => u.id).filter((id) => id !== user?.id);
    const allSelected = visibleIds.every((id) => next.has(id));
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected).filter((id) => id !== user?.id);
    if (ids.length === 0) {
      toast.error("Nothing selected");
      return;
    }
    if (!window.confirm(
      `Delete ${ids.length} user${ids.length === 1 ? "" : "s"}? This cannot be undone.`,
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} / ${data.requested} user${data.requested === 1 ? "" : "s"}`);
      clearSelection();
      // Step back a page if we just emptied the current one.
      if (users.length === ids.length && page > 1) setPage(page - 1);
      else load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Admin-only guard (client-side; server also enforces)
  if (user && user !== null && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <HubHeader />
      <Toaster />

      <main id="module-content" className="flex-1 relative">
        <div className="hero-watermark" aria-hidden="true" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14">
          <OperationsStatus />
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="mb-8"
            data-testid="admin-header"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-mono-meta text-muted-foreground hover:text-foreground transition-colors mb-4"
              data-testid="admin-back-hub"
            >
              <ArrowLeft size={12} strokeWidth={1.75} />
              Go to My work
            </Link>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="font-mono-meta text-muted-foreground mb-2 flex items-center gap-2">
                  <ShieldCheck size={12} strokeWidth={1.75} />
                  Global Administration
                </div>
                <h1 className="font-serif-display text-[40px] md:text-[52px] leading-tight tracking-tight">
                  Users &amp; access.
                </h1>
                <p className="text-foreground/70 text-base md:text-[17px] leading-[1.6] mt-3 max-w-2xl">
                  Grant roles, allocate module access, create colleague accounts.
                  Changes take effect on the user&apos;s next request.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInvite(true)}
                className="btn-primary"
                data-testid="admin-invite-open"
              >
                <UserPlus size={14} strokeWidth={1.75} />
                Create user
              </button>
            </div>
          </motion.header>

          <UsersTable
            users={users}
            loading={loading}
            saving={saving}
            currentUserId={user?.id}
            onFieldChange={updateLocal}
            onSave={persistUser}
            onDelete={removeUser}
            page={page}
            pageSize={pageSize}
            totalUsers={totalUsers}
            totalPages={totalPages}
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            onPage={setPage}
            onPageSize={(n) => { setPageSize(n); setPage(1); }}
            selected={selected}
            onToggleSelect={toggleSelect}
            onSelectAllVisible={selectAllVisible}
            onClearSelection={clearSelection}
            onBulkDelete={bulkDelete}
            bulkBusy={bulkBusy}
          />

          <details className="border p-4 my-6"><summary>Data quality and ownership exceptions</summary><DataQualityPanel /><LegacyArchivePanel /></details>
          <details className="border p-4 my-6"><summary>Access audit history</summary><AuditLogPanel /></details>
          <details className="border p-4 my-6"><summary>Production maintenance</summary><DangerZonePanel /></details>
        </div>
      </main>

      {showInvite && (
        <InviteUserDialog
          onClose={() => setShowInvite(false)}
          onCreated={() => {
            setShowInvite(false);
            setPage(1);
            load();
          }}
        />
      )}
    </div>
  );
}

function UsersTable({
  users, loading, saving, currentUserId, onFieldChange, onSave, onDelete,
  page, pageSize, totalUsers, totalPages, search, onSearchChange, onPage, onPageSize,
  selected, onToggleSelect, onSelectAllVisible, onClearSelection, onBulkDelete, bulkBusy,
}) {
  const selectableIds = users.map((u) => u.id).filter((id) => id !== currentUserId);
  const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  return (
    <>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          aria-label="Search users by email or name"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="border border-border bg-card px-3 py-2 text-sm w-72 focus:outline-none focus:border-foreground"
          data-testid="admin-users-search"
        />
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          {loading ? "Loading…" : `${totalUsers} user${totalUsers === 1 ? "" : "s"} · page ${page}/${Math.max(totalPages, 1)}`}
        </div>
        {selected.size > 0 && (
          <div
            className="ml-auto inline-flex items-center gap-2 border border-destructive/40 bg-destructive/10 px-2 py-1"
            data-testid="admin-users-bulk-bar"
          >
            <span className="text-xs font-mono text-destructive">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className="text-xs px-2 py-0.5 border border-border bg-background hover:border-foreground"
              data-testid="admin-users-bulk-clear"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onBulkDelete}
              disabled={bulkBusy}
              className="text-xs px-2 py-0.5 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="admin-users-bulk-delete"
            >
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </button>
          </div>
        )}
      </div>
      {loading ? (
        <div className="border border-border bg-card p-10 text-center font-mono-meta text-muted-foreground" data-testid="admin-users-loading">
          Loading users…
        </div>
      ) : users.length === 0 ? (
        <div className="border border-border bg-card p-10 text-center font-mono-meta text-muted-foreground">
          No users match this filter.
        </div>
      ) : (
        <>
          <div className="border border-border bg-card overflow-x-auto" tabIndex={0} role="region" aria-label="Users and actions" data-testid="admin-users-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-secondary/60">
                  <th className="w-8 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={onSelectAllVisible}
                      data-testid="admin-users-select-all"
                      title="Select every user on this page (except your own account)"
                    />
                  </th>
                  <th className="font-mono-meta text-muted-foreground px-4 py-3">Email</th>
                  <th className="font-mono-meta text-muted-foreground px-4 py-3">Name</th>
                  <th className="font-mono-meta text-muted-foreground px-4 py-3">Role</th>
                  <th className="font-mono-meta text-muted-foreground px-4 py-3">Modules</th>
                  <th className="font-mono-meta text-muted-foreground px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isSelf={u.id === currentUserId}
                    saving={!!saving[u.id]}
                    onFieldChange={onFieldChange}
                    onSave={onSave}
                    onDelete={onDelete}
                    checked={selected.has(u.id)}
                    onToggleCheck={() => onToggleSelect(u.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <TablePager
            page={page}
            totalPages={totalPages}
            onPage={onPage}
            totalItems={totalUsers}
            pageSize={pageSize}
            pageSizeOptions={[15, 30, 50, 100]}
            onPageSize={onPageSize}
            testidPrefix="admin-users"
          />
        </>
      )}
    </>
  );
}

function UserRow({ user, isSelf, saving, onFieldChange, onSave, onDelete, checked, onToggleCheck }) {
  const toggleModule = (m) => {
    const set = new Set(user.modules || []);
    if (set.has(m)) set.delete(m);
    else set.add(m);
    onFieldChange(user.id, { modules: Array.from(set) });
  };

  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors">
      <td className="w-8 px-3 py-3 align-top">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggleCheck}
          disabled={isSelf}
          data-testid={`admin-users-select-${user.id}`}
          title={isSelf ? "You can't bulk-delete your own account" : "Select for bulk delete"}
        />
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        <div className="font-medium truncate max-w-[220px]" title={user.email}>
          {user.email}
        </div>
        {user.sso_provisioned && (
          <span className="font-mono-meta text-[9px] text-muted-foreground">SSO</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-foreground truncate max-w-[180px]">
        {user.name || <span className="text-muted-foreground italic">unnamed</span>}
      </td>
      <td className="px-4 py-3">
        <select
          value={user.role}
          onChange={(e) => onFieldChange(user.id, { role: e.target.value })}
          disabled={isSelf}
          className="border border-border bg-card px-2 py-1 text-sm text-foreground focus:outline-none focus:border-foreground disabled:opacity-60"
          data-testid={`role-select-${user.id}`}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <AccountAccessSettings user={user} isSelf={isSelf} onFieldChange={onFieldChange} />
        {isSelf && (
          <div className="font-mono-meta text-[9px] text-muted-foreground mt-1">you</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2">
          {MODULES.map((m) => {
            const enabled = (user.modules || []).includes(m.key);
            return (
              <button
                key={m.key}
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={`${enabled ? "Disable" : "Enable"} ${m.label} module access`}
                onClick={() => toggleModule(m.key)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    toggleModule(m.key);
                  }
                }}
                data-testid={`module-${m.key}-${user.id}`}
                className={`inline-flex items-center gap-1.5 border px-2 py-0.5 cursor-pointer text-sm select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-1 ${
                  enabled
                    ? "border-foreground bg-foreground text-background"
                    : "border-border text-muted-foreground hover:border-foreground"
                }`}
              >
                <span aria-hidden="true" className="text-[10px] font-mono w-3 inline-block">
                  {enabled ? "✓" : ""}
                </span>
                {m.label}
              </button>
            );
          })}
        </div>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => onSave(user)}
          disabled={saving}
          className="btn-ghost mr-2 !py-1.5 !text-[11px]"
          data-testid={`save-user-${user.id}`}
        >
          <Save size={12} strokeWidth={1.75} />
          {saving ? "…" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(user)}
          disabled={isSelf}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-destructive/60 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[11px] font-mono-meta uppercase tracking-wider"
          data-testid={`delete-user-${user.id}`}
        >
          <Trash2 size={12} strokeWidth={1.75} />
          Delete
        </button>
      </td>
    </tr>
  );
}

function InviteUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    company_id: "stabicon",
    name: "",
    password: "",
    role: "viewer",
    modules: [],
  });
  const [busy, setBusy] = useState(false);

  const toggleModule = (m) => {
    setForm((f) => {
      const set = new Set(f.modules);
      if (set.has(m)) set.delete(m);
      else set.add(m);
      return { ...f, modules: Array.from(set) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`Invited ${data.email}`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalSurface
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onDismiss={onClose}
      data-testid="admin-invite-dialog"
    >
      <form

        onSubmit={submit}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card border border-border p-6 md:p-8"
      >
        <div className="font-mono-meta text-muted-foreground mb-2">Invite a colleague</div>
        <h2 className="font-serif-display text-2xl leading-tight mb-6">New user</h2>
        <label className="block mb-4">Owning company<select className="input" required value={form.company_id} onChange={e=>setForm(f=>({...f,company_id:e.target.value}))}><option value="stabicon">Stabicon</option><option value="lyrus">Lyrus</option></select></label>
        <label className="block mb-4">Access template<select className="input" defaultValue="" onChange={e=>{const templates={filer:{role:'user',modules:['dossiers','formulations','partners']},scientist:{role:'scientist',modules:['formulations','clinical-intelligence','dossiers']},commercial:{role:'user',modules:['crm','portfolio','formulations']},sourcing:{role:'user',modules:['partners','formulations']},viewer:{role:'viewer',modules:['crm','portfolio','formulations','partners','dossiers']}};if(templates[e.target.value])setForm(f=>({...f,...templates[e.target.value]}));}}><option value="">Choose a role template or customise below</option><option value="filer">Dossier filer</option><option value="scientist">Scientist / researcher</option><option value="commercial">Commercial team</option><option value="sourcing">Sourcing team</option><option value="viewer">Read-only reviewer</option></select></label>

        <label className="block mb-4">
          <div className="font-mono-meta text-muted-foreground mb-1">Email</div>
          <input
            type="email"
            required
            className="input"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="invite-email"
          />
        </label>

        <label className="block mb-4">
          <div className="font-mono-meta text-muted-foreground mb-1">Full name</div>
          <input
            type="text"
            required
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="invite-name"
          />
        </label>

        <label className="block mb-4">
          <div className="font-mono-meta text-muted-foreground mb-1">Initial password</div>
          <input
            type="text"
            required
            minLength={8}
            className="input font-mono"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            data-testid="invite-password"
          />
          <span className="text-[11px] text-muted-foreground">Min 8 chars. Share it privately with the invitee.</span>
        </label>

        <label className="block mb-4">
          <div className="font-mono-meta text-muted-foreground mb-1">Role</div>
          <select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="input"
            data-testid="invite-role"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>

        <div className="block mb-6">
          <div className="font-mono-meta text-muted-foreground mb-2">Module access</div>
          <div className="flex flex-wrap gap-2">
            {MODULES.map((m) => {
              const enabled = form.modules.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`${enabled ? "Disable" : "Enable"} ${m.label} module access`}
                  onClick={() => toggleModule(m.key)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      toggleModule(m.key);
                    }
                  }}
                  data-testid={`invite-module-${m.key}`}
                  className={`inline-flex items-center gap-1.5 border px-3 py-1 cursor-pointer text-sm select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-1 ${
                    enabled
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-muted-foreground hover:border-foreground"
                  }`}
                >
                  <span aria-hidden="true" className="text-[10px] font-mono w-3 inline-block">
                    {enabled ? "✓" : ""}
                  </span>
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="invite-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="invite-submit">
            {busy ? "Creating…" : "Create account"}
          </button>
        </div>
      </form>
    </ModalSurface>
  );
}

/**
 * Compact audit log panel — shown under the Users table.
 * Reads /api/admin/audit and lists recent invite / update / delete events.
 */
function AuditLogPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "invite" | "update" | "delete" | "reset"
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { action: filter };
      const { data } = await api.get("/admin/audit", {
        params: { ...params, page, page_size: pageSize },
      });
      setEvents(data.items || data.events || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      setEvents([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [filter, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 whenever the action filter changes.
  useEffect(() => { setPage(1); }, [filter]);

  const ACTION_LABEL = { invite: "Invited", update: "Updated", delete: "Deleted", reset: "Reset" };
  const ACTION_COLOR = { invite: "text-success", update: "text-info", delete: "text-destructive", reset: "text-destructive" };

  return (
    <section className="mt-12" data-testid="admin-audit-section">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="font-mono-meta text-muted-foreground mb-2 flex items-center gap-2">
            <ScrollText size={12} strokeWidth={1.75} />
            Audit Log
          </div>
          <h2 className="font-serif-display text-[28px] md:text-[32px] leading-tight tracking-tight">
            Who changed what.
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 font-mono-meta" data-testid="audit-filter">
          {["all", "invite", "update", "delete", "reset"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 border transition-colors ${
                filter === f
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
              data-testid={`audit-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto" tabIndex={0} role="region" aria-label="Audit records" data-testid="admin-audit-table">
        {loading ? (
          <div className="p-10 text-center font-mono-meta text-muted-foreground">Loading audit events…</div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center font-mono-meta text-muted-foreground">
            No audit events yet.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-secondary/60">
                <th className="font-mono-meta text-muted-foreground px-4 py-3">When</th>
                <th className="font-mono-meta text-muted-foreground px-4 py-3">Action</th>
                <th className="font-mono-meta text-muted-foreground px-4 py-3">Actor</th>
                <th className="font-mono-meta text-muted-foreground px-4 py-3">Target</th>
                <th className="font-mono-meta text-muted-foreground px-4 py-3">Change</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr
                  key={`${e.created_at}-${i}`}
                  className="border-b border-border last:border-b-0"
                  data-testid={`audit-row-${i}`}
                >
                  <td className="px-4 py-3 font-mono-meta text-muted-foreground text-[11px]">
                    {formatWhen(e.created_at)}
                  </td>
                  <td className={`px-4 py-3 font-mono-meta ${ACTION_COLOR[e.action] || ""}`}>
                    {ACTION_LABEL[e.action] || e.action}
                  </td>
                  <td className="px-4 py-3 text-sm truncate max-w-[220px]" title={e.actor_email}>
                    {e.actor_email || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm truncate max-w-[220px]" title={e.target_email}>
                    {e.target_email || e.target_id || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{summariseChange(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <TablePager
        page={page}
        totalPages={totalPages}
        onPage={setPage}
        totalItems={total}
        pageSize={pageSize}
        testidPrefix="admin-audit"
      />
    </section>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function summariseChange(evt) {
  if (evt.action === "invite" && evt.after) {
    return `role=${evt.after.role}, modules=[${(evt.after.modules || []).join(", ")}]`;
  }
  if (evt.action === "update" && evt.after) {
    return Object.entries(evt.after).map(([k, v]) =>
      `${k}=${Array.isArray(v) ? `[${v.join(", ")}]` : v}`
    ).join(" · ");
  }
  if (evt.action === "delete" && evt.before) {
    return `was role=${evt.before.role}, modules=[${(evt.before.modules || []).join(", ")}]`;
  }
  return "—";
}


function DangerZonePanel() {
  return (
    <section className="pt-4 space-y-3" data-testid="admin-production-maintenance">
      <h2 className="text-base font-semibold">Fresh production start</h2>
      <p>A fresh start needs a verified recovery copy and a review of user access, shared records and attachments across all modules.</p>
      <p>Arrange this with your administrator. A change log alone cannot restore deleted records.</p>
    </section>
  );
}

let companyDirectory;
function AccountAccessSettings({user, isSelf, onFieldChange}) {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    if (!companyDirectory) companyDirectory = api.get('/admin/companies').catch(error => {companyDirectory = null; throw error;});
    companyDirectory.then(result => setCompanies(result.data.items || [])).catch(() => {});
  }, []);
  const roles = {partners: ['R&D Scientist', 'R&D Manager', 'QA', 'QA Manager', 'QA Head', 'QC', 'Procurement', 'Procurement Head', 'Procurement Manager', 'Finance', 'Finance Head', 'Regulatory', 'Viewer'], formulations: ['scientist', 'reviewer', 'viewer', 'qa', 'regulatory', 'analytical', 'formulation', 'stability', 'research'], dossiers: ['author', 'reviewer', 'viewer']};
  return <details className="mt-2 text-xs"><summary>Company and access</summary><div className="space-y-2 py-2">
    <label className="block">Company<select aria-label={`Company for ${user.email}`} className="block border p-1 max-w-[220px]" value={user.company_id || ''} onChange={event => onFieldChange(user.id, {company_id: event.target.value})}><option value="" disabled>Choose company</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name || 'Unnamed company'}</option>)}</select></label>
    <label className="block"><input type="checkbox" disabled={isSelf} checked={!user.disabled} onChange={event => onFieldChange(user.id, {disabled: !event.target.checked})} /> Account enabled</label>
    {user.role !== 'admin' && Object.entries(roles).filter(([module]) => (user.modules || []).includes(module)).map(([module, choices]) => <label className="block" key={module}>{module} role<select className="block border p-1" value={user.module_roles?.[module] || choices[0]} onChange={event => onFieldChange(user.id, {module_roles: {...user.module_roles, [module]: event.target.value}})}>{choices.map(role => <option key={role}>{role}</option>)}</select></label>)}
  </div></details>;
}


function OperationsStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const load = () => api.get('/admin/operations').then(result => setStatus(result.data)).catch(() => setStatus({status: 'attention_required', issues: ['Operational status is unavailable']}));
    load(); const timer = setInterval(load, 60000); return () => clearInterval(timer);
  }, []);
  if (!status) return null;
  return <div role={status.status === 'healthy' ? 'status' : 'alert'} className={`border p-3 mb-5 text-sm ${status.status === 'healthy' ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
    <strong>{status.status === 'healthy' ? 'System checks healthy' : 'System needs attention'}</strong>
    {status.backup_age_hours != null && <span> · Backup age: {status.backup_age_hours} hours</span>}
    {status.issues?.map(issue => <p key={issue}>{issue}</p>)}
  </div>;
}


function LegacyArchivePanel() {
  const [summary, setSummary] = useState(null);
  const [collection, setCollection] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const load = async () => {
    try { setSummary((await api.get("/admin/legacy-archive")).data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!collection) return;
    let active = true;
    api.get(`/admin/legacy-archive/${collection}`, { params: { page } }).then(({ data }) => { if (active) setItems(data.items); }).catch(e => { if (active) setError(formatApiErrorDetail(e.response?.data?.detail)); });
    return () => { active = false; };
  }, [collection, page]);
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(items, null, 2)], { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = `legacy-${collection}-page-${page}.json`; a.click(); URL.revokeObjectURL(url);
  };
  const total = summary?.collections.find(c => c.collection === collection)?.records || 0;
  return <section className="border rounded-lg p-6 my-8">
    <h2 className="text-xl font-semibold">Legacy archive</h2>
    <p>Original records and ownership evidence are retained for administrator review. Restoration requires a verified ownership decision.</p>
    <button className="btn-primary my-4" onClick={load}>Load archive summary</button>
    {summary && <><p>{summary.total.toLocaleString()} records retained</p><label>Collection <select value={collection} onChange={e => { setCollection(e.target.value); setPage(1); setItems([]); }}><option value="">Select collection</option>{summary.collections.map(c => <option key={c.collection} value={c.collection}>{c.collection} ({c.records})</option>)}</select></label></>}
    {collection && <><p>Page {page} of {Math.max(1, Math.ceil(total / 50))}</p><button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button> <button disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>Next</button> <button onClick={download} disabled={!items.length}>Export this page</button>{items.map((row, index) => <details key={index}><summary>{row.id || JSON.stringify(row._id)} · {row.legacy_archive.reason}</summary><pre className="overflow-auto max-h-96 text-xs">{JSON.stringify(row, null, 2)}</pre></details>)}</>}
    {error && <p role="alert">{error}</p>}
  </section>;
}

function DataQualityPanel(){
 const [module,setModule]=useState('formulations'),[page,setPage]=useState(1),[data,setData]=useState(null),[error,setError]=useState('');
 useEffect(()=>{let active=true;setData(null);setError('');api.get('/admin/data-quality',{params:{module,page}}).then(r=>{if(active)setData(r.data);}).catch(e=>{if(active)setError(formatApiErrorDetail(e.response?.data?.detail));});return()=>{active=false;};},[module,page]);
 return <section className="reusable-form border p-4 my-4"><h2>Master-data review queue</h2><label>Module<select value={module} onChange={e=>{setModule(e.target.value);setPage(1);}}><option value="formulations">Molecule Intelligence</option><option value="partners">Partners material classifications</option></select></label>{error&&<p role="alert">{error}</p>}{data&&<><p>{data.notice}</p><p>Page {page} · {data.total_records} active records · {data.items.length} records needing review on this page</p>{data.items.map(r=><article key={r.id} className="border-b py-3"><Link to={r.route}>{r.title}</Link><p>{r.issues.join(' · ')}</p></article>)}<button disabled={page===1} onClick={()=>setPage(page-1)}>Previous review page</button><button disabled={!data.has_more} onClick={()=>setPage(page+1)}>Next review page</button></>}</section>;
}
